// ── Enums / string unions ──────────────────────────────────────────────────────

export type BankWithdrawalWorkflowStatus =
  | 'Draft'
  | 'Submitted'
  | 'PendingApproval'
  | 'Approved'
  | 'Rejected'
  | 'Posted'
  | 'Cancelled';

export type BankWithdrawalPostingStatus = 'NotPosted' | 'Posted' | 'Cancelled';

// ── Status display helpers ────────────────────────────────────────────────────

export const BNK_WITHDRAWAL_WORKFLOW_STATUS_LABELS: Record<BankWithdrawalWorkflowStatus, string> = {
  Draft:          'Draft',
  Submitted:      'Submitted',
  PendingApproval:'Pending Approval',
  Approved:       'Approved',
  Rejected:       'Rejected',
  Posted:         'Posted',
  Cancelled:      'Cancelled',
};

export const BNK_WITHDRAWAL_WORKFLOW_STATUS_COLORS: Record<BankWithdrawalWorkflowStatus, string> = {
  Draft:          'bg-gray-100 text-gray-700',
  Submitted:      'bg-blue-100 text-blue-700',
  PendingApproval:'bg-yellow-100 text-yellow-700',
  Approved:       'bg-green-100 text-green-700',
  Rejected:       'bg-red-100 text-red-700',
  Posted:         'bg-purple-100 text-purple-700',
  Cancelled:      'bg-gray-200 text-gray-500',
};

// ── Detail DTO ────────────────────────────────────────────────────────────────

export interface BankWithdrawalDto {
  id:                    string;
  companyId:             string;
  locationId?:           string | null;
  costCenterId?:         string | null;
  financialYearCode:     string;
  financialPeriodCode:   string;
  withdrawalNumber:      string;
  withdrawalDate:        string;
  postingDate:           string;
  bankAccountId:         string;
  bankAccountCode:       string;
  bankAccountName:       string;
  bankGLAccountId?:      string | null;
  withdrawalType:        string;
  contraAccountId?:      string | null;
  contraAccountCode?:    string | null;
  contraAccountName?:    string | null;
  currencyCode:          string;
  exchangeRate:          number;
  amount:                number;
  baseAmount:            number;
  reference?:            string | null;
  narration?:            string | null;
  workflowStatus:        BankWithdrawalWorkflowStatus;
  postingStatus:         BankWithdrawalPostingStatus;
  submittedBy?:          string | null;
  submittedDate?:        string | null;
  approvedBy?:           string | null;
  approvedDate?:         string | null;
  rejectedBy?:           string | null;
  rejectedDate?:         string | null;
  rejectionReason?:      string | null;
  postedBy?:             string | null;
  postedDate?:           string | null;
  cancelledBy?:          string | null;
  cancelledDate?:        string | null;
  cancellationReason?:   string | null;
  isReversal:            boolean;
  originalWithdrawalId?: string | null;
  reversalWithdrawalId?: string | null;
  sourceGlJournalId?:    string | null;
  createdDate:           string;
}

// ── List row DTO ──────────────────────────────────────────────────────────────

export interface BankWithdrawalListDto {
  id:               string;
  withdrawalNumber: string;
  withdrawalDate:   string;
  postingDate:      string;
  bankAccountCode:  string;
  bankAccountName:  string;
  withdrawalType:   string;
  currencyCode:     string;
  amount:           number;
  baseAmount:       number;
  workflowStatus:   BankWithdrawalWorkflowStatus;
  postingStatus:    BankWithdrawalPostingStatus;
  reference?:       string | null;
  isReversal:       boolean;
  createdDate:      string;
}

// ── Filter DTO ────────────────────────────────────────────────────────────────

export interface BankWithdrawalFilterDto {
  companyId?:          string;
  locationId?:         string;
  costCenterId?:       string;
  financialYearCode?:  string;
  bankAccountId?:      string;
  withdrawalType?:     string;
  workflowStatus?:     string;
  postingStatus?:      string;
  dateFrom?:           string;
  dateTo?:             string;
  search?:             string;
  pageNumber?:         number;
  pageSize?:           number;
  sortBy?:             string;
  sortDescending?:     boolean;
}

// ── Create / Update requests ──────────────────────────────────────────────────

export interface CreateBankWithdrawalRequest {
  companyId:           string;
  locationId?:         string | null;
  costCenterId?:       string | null;
  financialYearCode:   string;
  financialPeriodCode: string;
  withdrawalDate:      string;
  postingDate:         string;
  bankAccountId:       string;
  withdrawalType:      string;
  contraAccountId?:    string | null;
  currencyCode:        string;
  exchangeRate:        number;
  amount:              number;
  reference?:          string | null;
  narration?:          string | null;
}

export interface UpdateBankWithdrawalRequest {
  withdrawalDate:     string;
  postingDate:        string;
  bankAccountId:      string;
  bankAccountCode:    string;
  bankAccountName:    string;
  bankGLAccountId?:   string | null;
  withdrawalType:     string;
  contraAccountId?:   string | null;
  contraAccountCode?: string | null;
  contraAccountName?: string | null;
  currencyCode:       string;
  exchangeRate:       number;
  amount:             number;
  reference?:         string | null;
  narration?:         string | null;
}
